import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  ReceiptLong as ChargesIcon,
} from '@mui/icons-material';
import { useTabScopedOpen } from '../../dashboard/TabPanelActiveContext';
import type { ChargeCategory, OrderChargeDraft } from '../../../types/sales.types';
import { formatCurrency } from '../../../utils/formatters/money';
import { parseLocaleDecimal } from '../../../utils/localeNumber';
import { DecimalNumberInput } from '../../ui';
import {
  calculateOrderChargesTotal,
  createEmptyOrderChargeDraft,
  getDefaultChargeCategoryCode,
} from '../../../utils/sales/order-charges.utils';
import SalesStepHint from '../shared/SalesStepHint/SalesStepHint';
import { sectionPhaseSx } from '../shared/salesStep2Phase.styles';
import * as S from './SalesStep2.styles';
import * as OC from './OrderChargesSection.styles';

interface OrderChargesSectionProps {
  charges: OrderChargeDraft[];
  categories: ChargeCategory[];
  unlocked?: boolean;
  editable?: boolean;
  showModifyLink?: boolean;
  onModify?: () => void;
  showContinueCta?: boolean;
  onContinue?: () => void;
  lockedHint?: string;
  onAddCharge: (charge: OrderChargeDraft) => void;
  onUpdateCharge: (chargeId: string, updates: Partial<OrderChargeDraft>) => void;
  onRemoveCharge: (chargeId: string) => void;
}

interface ChargeFormDialogProps {
  open: boolean;
  isEditing: boolean;
  draft: OrderChargeDraft;
  categories: ChargeCategory[];
  defaultCategoryCode: string;
  onClose: () => void;
  onSave: () => void;
  onDraftChange: (updates: Partial<OrderChargeDraft>) => void;
}

const ChargeFormDialog: React.FC<ChargeFormDialogProps> = ({
  open,
  isEditing,
  draft,
  categories,
  defaultCategoryCode,
  onClose,
  onSave,
  onDraftChange,
}) => {
  const scopedOpen = useTabScopedOpen(open);
  return (
    <Dialog open={scopedOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar cargo' : 'Agregar cargo'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel id="order-charge-category-label">Categoría</InputLabel>
            <Select
              labelId="order-charge-category-label"
              label="Categoría"
              value={draft.categoryCode || defaultCategoryCode}
              onChange={(event) => onDraftChange({ categoryCode: event.target.value })}
              autoFocus
              required
            >
              {categories.map((category) => (
                <MenuItem key={category.code} value={category.code}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DecimalNumberInput
            label="Monto"
            required
            value={draft.amount > 0 ? String(draft.amount) : ''}
            onChange={(value) => {
              const parsed = value == null ? null : parseLocaleDecimal(String(value));
              onDraftChange({ amount: parsed == null ? 0 : Number(parsed) });
            }}
            decimalPlaces={2}
            min={0.01}
            max={999999.99}
            fullWidth
          />

          <TextField
            size="small"
            label="Descripción"
            placeholder="Ej. engaste, envío, servicio extra"
            value={draft.description ?? ''}
            onChange={(event) => onDraftChange({ description: event.target.value })}
            inputProps={{ maxLength: 500 }}
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <S.ModalCancelButton onClick={onClose}>Cancelar</S.ModalCancelButton>
        <S.ModalActionButton variant="contained" onClick={onSave} disabled={draft.amount <= 0}>
          {isEditing ? 'Guardar' : 'Agregar'}
        </S.ModalActionButton>
      </DialogActions>
    </Dialog>
  );
};

const OrderChargesSection: React.FC<OrderChargesSectionProps> = ({
  charges,
  categories,
  unlocked = true,
  editable = true,
  showModifyLink = false,
  onModify,
  showContinueCta = false,
  onContinue,
  lockedHint = 'Confirma las piezas para gestionar cargos adicionales',
  onAddCharge,
  onUpdateCharge,
  onRemoveCharge,
}) => {
  const defaultCategoryCode = useMemo(() => getDefaultChargeCategoryCode(categories), [categories]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChargeId, setEditingChargeId] = useState<string | null>(null);
  const [draft, setDraft] = useState<OrderChargeDraft>(() =>
    createEmptyOrderChargeDraft(defaultCategoryCode),
  );

  const categoryLabelByCode = useMemo(
    () => new Map(categories.map((category) => [category.code, category.label])),
    [categories],
  );

  const chargesTotal = useMemo(() => calculateOrderChargesTotal(charges), [charges]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }
    if (editingChargeId == null) {
      setDraft(createEmptyOrderChargeDraft(defaultCategoryCode));
    }
  }, [dialogOpen, editingChargeId, defaultCategoryCode]);

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingChargeId(null);
    setDraft(createEmptyOrderChargeDraft(defaultCategoryCode));
  };

  const openCreateDialog = () => {
    setEditingChargeId(null);
    setDraft(createEmptyOrderChargeDraft(defaultCategoryCode));
    setDialogOpen(true);
  };

  const openEditDialog = (charge: OrderChargeDraft) => {
    setEditingChargeId(charge.id);
    setDraft({ ...charge });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (draft.amount <= 0) {
      return;
    }

    const normalized: OrderChargeDraft = {
      ...draft,
      categoryCode: draft.categoryCode || defaultCategoryCode,
      description: draft.description?.trim() ?? '',
    };

    if (editingChargeId) {
      onUpdateCharge(editingChargeId, normalized);
    } else {
      onAddCharge(normalized);
    }

    closeDialog();
  };

  return (
    <>
      <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
        <CardContent
          sx={charges.length === 0 && !showContinueCta ? { '&:last-child': { pb: 2 } } : undefined}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              mb: unlocked && (charges.length > 0 || editable) ? 2 : 0,
            }}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChargesIcon color="primary" />
              Cargos adicionales
            </Typography>
            {showModifyLink && onModify && (
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onModify}
                sx={{ cursor: 'pointer' }}
              >
                Modificar cargos
              </Link>
            )}
          </Box>

          <Box sx={sectionPhaseSx(unlocked)}>
            {!unlocked && <SalesStepHint tone="info">{lockedHint}</SalesStepHint>}

            {unlocked && (
              <>
                {editable && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mb: charges.length > 0 ? 2 : 0,
                    }}
                  >
                    <S.SubStepGhostContinueButton
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                      onClick={openCreateDialog}
                      sx={{ mt: 0, py: 0.75, px: 1.5 }}
                    >
                      Agregar cargo
                    </S.SubStepGhostContinueButton>
                  </Box>
                )}

                {charges.length === 0 && !editable && (
                  <Typography variant="body2" color="text.secondary">
                    Sin cargos adicionales.
                  </Typography>
                )}

                {charges.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {charges.map((charge) => (
                      <S.PricingLineCard key={charge.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {categoryLabelByCode.get(charge.categoryCode) ?? charge.categoryCode}
                            </Typography>
                            {charge.description?.trim() ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25 }}
                              >
                                {charge.description.trim()}
                              </Typography>
                            ) : null}
                          </Box>
                          <OC.ChargeRowActions>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {formatCurrency(charge.amount)}
                            </Typography>
                            {editable && (
                              <>
                                <IconButton
                                  size="small"
                                  aria-label="Editar cargo"
                                  onClick={() => openEditDialog(charge)}
                                  sx={{ color: 'text.secondary' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  aria-label="Eliminar cargo"
                                  onClick={() => onRemoveCharge(charge.id)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </OC.ChargeRowActions>
                        </Box>
                      </S.PricingLineCard>
                    ))}

                    <S.PricingWaterfall sx={{ mt: 0.5 }}>
                      <S.PricingWaterfallRow tone="final">
                        <Typography variant="body2" color="text.secondary">
                          Total cargos
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                          {formatCurrency(chargesTotal)}
                        </Typography>
                      </S.PricingWaterfallRow>
                    </S.PricingWaterfall>
                  </Box>
                )}

                {showContinueCta && onContinue && (
                  <S.SubStepGhostContinueButton
                    fullWidth
                    onClick={onContinue}
                    startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                    sx={{ mt: 1.5 }}
                  >
                    Continuar a configuración de pago
                  </S.SubStepGhostContinueButton>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <ChargeFormDialog
        open={dialogOpen}
        isEditing={editingChargeId != null}
        draft={draft}
        categories={categories}
        defaultCategoryCode={defaultCategoryCode}
        onClose={closeDialog}
        onSave={handleSave}
        onDraftChange={(updates) => setDraft((prev) => ({ ...prev, ...updates }))}
      />
    </>
  );
};

export default OrderChargesSection;
